import React, { useState } from 'react';
import BlochSphereCanvas from './BlochSphereCanvas.jsx';
import { X, H } from './quantum/gates';
import { zeroState, applyGate, applyControlledGate, tensorProduct, measureQubit, blochVectorForQubit } from './quantum/QuantumState';

/**
 * Builds the real 3-qubit joint state this page demonstrates: qubits 0 and
 * 1 in the entangled state Ψ = (|01⟩+|10⟩)/√2 (anti-correlated — measuring
 * either always gives the opposite of the other), tensored with qubit 2 in
 * |+⟩, completely independent of the pair. Everything this page shows
 * (Bloch vectors, probabilities, measurement results) is read from this one
 * state via src/quantum/, not scripted separately per qubit.
 */
const buildInitialState = () => {
  let pair = zeroState(2);
  pair = applyGate(pair, X, 1, 2); // |01>
  pair = applyGate(pair, H, 0, 2); // (|01>+|11>)/sqrt(2)
  pair = applyControlledGate(pair, X, 0, 1, 2); // CNOT(0->1): (|01>+|10>)/sqrt(2)
  const independent = applyGate(zeroState(1), H, 0, 1); // |+>
  return tensorProduct(pair, independent);
};

const QUBIT_META = [
  { label: 'Qubit 1', badge: 'Entangled', accent: 'purple' },
  { label: 'Qubit 2', badge: 'Entangled', accent: 'pink' },
  { label: 'Qubit 3', badge: 'Independent', accent: 'gray' },
];

const ACCENT_CLASSES = {
  purple: { title: 'text-purple-700', badge: 'bg-purple-100 text-purple-700', button: 'bg-purple-600 hover:bg-purple-700', result: 'bg-purple-50 border-purple-100', text: 'text-purple-600' },
  pink: { title: 'text-pink-700', badge: 'bg-pink-100 text-pink-700', button: 'bg-pink-600 hover:bg-pink-700', result: 'bg-pink-50 border-pink-100', text: 'text-pink-600' },
  gray: { title: 'text-gray-700', badge: 'bg-gray-100 text-gray-600', button: 'bg-gray-600 hover:bg-gray-700', result: 'bg-gray-50 border-gray-200', text: 'text-gray-500' },
};

export default function ThreeQubitSystem() {
  const [state, setState] = useState(buildInitialState);
  const [measured, setMeasured] = useState([false, false, false]);
  const [results, setResults] = useState([null, null, null]);

  const allMeasured = measured.every(Boolean);
  const entangledMeasured = measured[0] && measured[1];

  const blochVectors = [0, 1, 2].map((q) => blochVectorForQubit(state, 3, q));
  // P(0) for each qubit from its own marginal — correct even for a mixed
  // (entangled, not-yet-measured) qubit: Tr(rho * |0><0|) = (1+z)/2.
  // Clamp to [0,1]: z can be a hair outside [-1,1] due to floating-point
  // rounding (e.g. -1.0000000000000002), which would otherwise render as
  // a stray "-0%" once collapsed to a pole.
  const p0s = blochVectors.map((v) => Math.min(1, Math.max(0, (1 + v.z) / 2)));

  const handleMeasureQubit = (q) => {
    if (measured[q]) return;
    const { result, state: newState } = measureQubit(state, 3, q);
    setState(newState);
    setMeasured((m) => m.map((v, i) => (i === q ? true : v)));
    setResults((r) => r.map((v, i) => (i === q ? result : v)));
  };

  const handleReset = () => {
    setState(buildInitialState());
    setMeasured([false, false, false]);
    setResults([null, null, null]);
  };

  return (
    <div className="min-h-screen bg-gray-50 p-4">
      <div className="max-w-7xl mx-auto">
        {/* Title */}
        <div className="text-center mb-4">
          <h1 className="text-3xl font-bold text-gray-900 mb-1">
            3-Qubit System
          </h1>
          <p className="text-lg text-gray-600 mb-1">2 Entangled + 1 Independent</p>
          <p className="text-sm text-gray-500">
            {allMeasured ? '⚡ All Measurements Complete' : '🌌 Qubits in Superposition'}
          </p>
        </div>

        {/* System Diagram */}
        <div className="mb-4 bg-white rounded-xl p-4 border border-gray-200 shadow-sm">
          <h3 className="text-gray-800 font-bold text-lg mb-3 text-center">System Configuration</h3>
          <div className="flex justify-center items-center gap-4 text-white text-sm">
            <div className="flex items-center gap-2">
              <div className="w-12 h-12 rounded-full bg-purple-600 flex items-center justify-center font-bold border-2 border-purple-300 text-xs">
                Q1
              </div>
              <div className="text-purple-600 text-2xl">⟷</div>
              <div className="w-12 h-12 rounded-full bg-pink-600 flex items-center justify-center font-bold border-2 border-pink-300 text-xs">
                Q2
              </div>
            </div>
            <div className="text-gray-300 text-3xl px-4">|</div>
            <div className="w-12 h-12 rounded-full bg-gray-500 flex items-center justify-center font-bold border-2 border-gray-300 text-xs">
              Q3
            </div>
          </div>
          <div className="flex justify-center gap-24 mt-2 text-xs">
            <div className="text-purple-700 font-semibold">Entangled Pair</div>
            <div className="text-gray-500 font-semibold">Independent</div>
          </div>
          <p className="text-center text-xs text-gray-500 mt-3 font-mono">|ψ₁₂⟩ = (|01⟩ + |10⟩)/√2 &nbsp;⊗&nbsp; |ψ₃⟩ = |+⟩</p>
        </div>

        {/* Qubits Display */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
          {[0, 1, 2].map((q) => {
            const meta = QUBIT_META[q];
            const cls = ACCENT_CLASSES[meta.accent];
            const p0 = p0s[q] * 100;
            const p1 = 100 - p0;
            return (
              <div key={q} className="bg-white rounded-xl p-3 border border-gray-200 shadow-sm">
                <div className="flex items-center justify-between mb-2">
                  <h3 className={`text-base font-bold ${cls.title}`}>{meta.label}</h3>
                  <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${cls.badge}`}>{meta.badge}</span>
                </div>

                <div className="flex justify-center mb-2">
                  <BlochSphereCanvas vector={blochVectors[q]} size={120} showMixed />
                </div>

                <div className="text-center space-y-1">
                  <div className="grid grid-cols-2 gap-1 text-xs">
                    <div className="bg-cyan-50 rounded p-1 border border-cyan-100">
                      <div className="text-cyan-600 text-xs">P(|0⟩)</div>
                      <div className="text-cyan-700 font-bold text-sm">{p0.toFixed(0)}%</div>
                    </div>
                    <div className="bg-pink-50 rounded p-1 border border-pink-100">
                      <div className="text-pink-600 text-xs">P(|1⟩)</div>
                      <div className="text-pink-700 font-bold text-sm">{p1.toFixed(0)}%</div>
                    </div>
                  </div>

                  {!measured[q] && (
                    <button
                      onClick={() => handleMeasureQubit(q)}
                      className={`w-full mt-2 px-2 py-1.5 text-white font-bold text-xs rounded-lg shadow-sm transform hover:scale-105 transition-all duration-200 ${cls.button}`}
                    >
                      Measure {meta.label.replace('Qubit', 'Q').replace(' ', '')}
                    </button>
                  )}

                  {measured[q] && (
                    <div className={`mt-2 rounded-lg p-2 border ${cls.result}`}>
                      <div className="text-gray-500 text-xs mb-1">Result</div>
                      <div className={`text-2xl font-bold ${results[q] === 0 ? 'text-cyan-600' : 'text-pink-600'}`}>
                        {results[q]}
                      </div>
                      <div className={`text-xs ${cls.text}`}>State: |{results[q]}⟩</div>
                    </div>
                  )}
                  {q < 2 && measured[q ^ 1] && !measured[q] && (
                    <p className="text-[11px] text-amber-700 mt-1 italic">
                      Its Bloch sphere already snapped to a definite direction — its partner's measurement determined it instantly.
                    </p>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* Behavior Explanation */}
        {entangledMeasured && !measured[2] && (
          <div className="mb-4 bg-amber-50 rounded-xl p-4 border border-amber-200">
            <h3 className="text-amber-800 font-bold text-sm mb-2 text-center">
              ⚠️ Notice: Entangled vs Independent Behavior
            </h3>
            <p className="text-amber-800 text-center text-sm">
              Q1 and Q2 collapsed together to opposite states ({results[0]} and {results[1]}).
              But Q3 is still in superposition! It's completely independent and unaffected by the measurement of Q1 and Q2.
            </p>
          </div>
        )}

        {/* Results Summary */}
        {allMeasured && (
          <div className="mb-4 bg-white rounded-xl p-4 border border-gray-200 shadow-sm">
            <h3 className="text-gray-800 font-bold text-lg mb-3 text-center">Complete System State</h3>
            <div className="flex justify-center items-center gap-4 text-sm">
              <div className="text-center">
                <div className="text-purple-600 text-xs mb-1">Entangled Pair</div>
                <div className="flex items-center gap-2">
                  <div className={`text-2xl font-bold ${results[0] === 0 ? 'text-cyan-600' : 'text-pink-600'}`}>{results[0]}</div>
                  <div className="text-purple-500 text-lg">⟷</div>
                  <div className={`text-2xl font-bold ${results[1] === 0 ? 'text-cyan-600' : 'text-pink-600'}`}>{results[1]}</div>
                </div>
                <div className="text-purple-600 text-xs mt-1">
                  {results[0] !== results[1] ? '✓ Anti-correlated' : '✗ Error'}
                </div>
              </div>

              <div className="text-gray-300 text-2xl">|</div>

              <div className="text-center">
                <div className="text-gray-500 text-xs mb-1">Independent</div>
                <div className={`text-2xl font-bold ${results[2] === 0 ? 'text-cyan-600' : 'text-pink-600'}`}>{results[2]}</div>
                <div className="text-gray-500 text-xs mt-1">Random result</div>
              </div>
            </div>
          </div>
        )}

        {/* Control Button */}
        <div className="flex justify-center mb-4">
          <button
            onClick={handleReset}
            className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white font-bold text-sm rounded-xl shadow-md transform hover:scale-105 transition-all duration-200"
          >
            ↻ Reset All Qubits
          </button>
        </div>

        {/* Educational Info */}
        <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm">
          <h3 className="font-bold text-lg mb-2 text-gray-900">
            🧬 Key Behaviors
          </h3>
          <div className="grid md:grid-cols-2 gap-4 text-gray-600 text-sm">
            <div>
              <h4 className="text-purple-700 font-semibold mb-1">Entangled Qubits (Q1 & Q2):</h4>
              <ul className="space-y-1 text-xs">
                <li>• Share the same wavefunction: |ψ₁₂⟩ = (|01⟩ + |10⟩)/√2</li>
                <li>• Alone, each has <strong>no definite Bloch direction</strong> — its Bloch sphere shows "mixed"</li>
                <li>• Measuring one <strong>instantly</strong> determines the other</li>
                <li>• Always produce <strong>opposite</strong> results (anti-correlated)</li>
                <li>• Cannot be described independently</li>
              </ul>
            </div>
            <div>
              <h4 className="text-gray-700 font-semibold mb-1">Independent Qubit (Q3):</h4>
              <ul className="space-y-1 text-xs">
                <li>• Has its own separate wavefunction: |ψ₃⟩ = |+⟩</li>
                <li>• Has a well-defined Bloch direction the whole time</li>
                <li>• <strong>NOT affected</strong> by measurements of Q1 or Q2</li>
                <li>• Produces <strong>random</strong> results (no correlation)</li>
                <li>• Can be described completely on its own</li>
              </ul>
            </div>
          </div>
          <div className="mt-3 p-3 bg-blue-50 rounded-lg border border-blue-100">
            <p className="text-blue-800 text-xs text-center">
              <strong>Key Insight:</strong> Entanglement is a special quantum connection that must be created.
              By default, qubits are independent. Notice how Q1 and Q2 behave as a single system — even the Bloch
              sphere of the one you didn't click updates the instant you measure its partner — while Q3 maintains
              its independence!
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
